import React, { useState, useEffect } from "react";
import { getVATReport } from "./vatService";
import "./VATReport.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const VATReportPage = () => {
  const { canView, canExport } = usePermissions();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!canView("vat")) {
    return <AccessDenied moduleName="VAT Report" action="view" />;
  }

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVATReport(startDate, endDate);
      setReportData(data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch VAT report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, []);

  const formatAmount = (num) => {
    return num != null ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
  };

  return (
    <div className="vat-report-container">
      <div className="vat-report-header">
        <h2>UAE VAT Format Report</h2>
      </div>

      <div className="vat-filters">
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button className="btn-fetch" onClick={fetchReport}>
          Fetch Report
        </button>
      </div>

      {loading && <p>Loading VAT data...</p>}
      {error && <p className="error-text">{error}</p>}

      {reportData && (
        <div className="vat-report-content">
          {/* Outputs */}
          <div className="vat-section">
            <h3>VAT on Sales and All Other Outputs</h3>
            <table className="vat-table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Description</th>
                  <th className="text-right">Amount (AED)</th>
                  <th className="text-right">VAT Amount (AED)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Standard rated supplies</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_1_standard_rated_supplies.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_1_standard_rated_supplies.vat)}</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Tax Refunds provided to Tourists</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_2_tax_refunds_tourists.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_2_tax_refunds_tourists.vat)}</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Supplies subject to the reverse charge provisions</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_3_supplies_reverse_charge.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_3_supplies_reverse_charge.vat)}</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>Zero rated supplies</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_4_zero_rated_supplies.amount)}</td>
                  <td className="text-right">-</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>Exempt supplies</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_5_exempt_supplies.amount)}</td>
                  <td className="text-right">-</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>Goods imported into the UAE</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_6_goods_imported_uae.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_6_goods_imported_uae.vat)}</td>
                </tr>
                <tr>
                  <td>7</td>
                  <td>Adjustments to goods imported into the UAE</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_7_adjustments_goods_imported.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_7_adjustments_goods_imported.vat)}</td>
                </tr>
                <tr className="vat-total-row">
                  <td>8</td>
                  <td>Total Outputs</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_8_total_outputs.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_sales_and_all_other_outputs.box_8_total_outputs.vat)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Inputs */}
          <div className="vat-section">
            <h3>VAT on Expenses and All Other Inputs</h3>
            <table className="vat-table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Description</th>
                  <th className="text-right">Amount (AED)</th>
                  <th className="text-right">Recoverable VAT (AED)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>9</td>
                  <td>Standard rated expenses</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_9_standard_rated_expenses.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_9_standard_rated_expenses.vat)}</td>
                </tr>
                <tr>
                  <td>10</td>
                  <td>Supplies subject to the reverse charge provisions</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_10_supplies_reverse_charge.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_10_supplies_reverse_charge.vat)}</td>
                </tr>
                <tr className="vat-total-row">
                  <td>12</td>
                  <td>Total Inputs</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_12_total_inputs.amount)}</td>
                  <td className="text-right">{formatAmount(reportData.vat_on_expenses_and_all_other_inputs.box_12_total_inputs.vat)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Net VAT Due */}
          <div className="vat-section">
            <h3>Net VAT Due</h3>
            <table className="vat-table summary-table">
              <tbody>
                <tr>
                  <td>13</td>
                  <td>Total VAT due for current period</td>
                  <td className="text-right">{formatAmount(reportData.net_vat_due.box_13_total_vat_due)}</td>
                </tr>
                <tr>
                  <td>14</td>
                  <td>Total VAT recoverable for current period</td>
                  <td className="text-right">{formatAmount(reportData.net_vat_due.box_14_total_vat_recoverable)}</td>
                </tr>
                <tr className="vat-payable-row">
                  <td>15</td>
                  <td>Payable Tax for the period</td>
                  <td className="text-right">{formatAmount(reportData.net_vat_due.box_15_payable_tax)}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      )}
    </div>
  );
};

export default VATReportPage;
